import React, {
	useEffect,
	useState,
	useCallback,
	useImperativeHandle,
	type ChangeEvent,
	type ElementRef,
	type ReactNode,
	type KeyboardEvent,
	type FocusEvent,
	type FormEvent,
} from 'react';
import noop from 'lodash/noop';
import { useRegisterNudgeTarget } from '@atlassiansox/nudge-tooltip';
import { isSafeUrl } from '@atlaskit/adf-schema';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Textfield, { type TextFieldProps } from '@atlaskit/textfield';
import { ConditionalWrapper } from '@atlassian/jira-business-conditional-wrapper/src/index.tsx';
import { TextCellWrapper } from '@atlassian/jira-business-fields/src/common/cell-wrapper/index.tsx';
import { FixItErrorFlag } from '@atlassian/jira-business-fields/src/common/ui/fixit-error-flag/index.tsx';
import { useSimpleSearchHighlight } from '@atlassian/jira-business-filters/src/controllers/simple-search/index.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useEditField } from '@atlassian/jira-issue-field-base/src/services/edit-field-service/main.tsx';
import { LazySingleLineSmartLink } from '@atlassian/jira-issue-field-smart-link/src/ui/single-line-smart-link/async.tsx';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import HighlightText from '@atlassian/jira-software-highlight-text/src/ui/index.tsx';
import { INLINE_EDIT_LIST_NUDGE_ID } from '@atlassian/jira-software-onboarding-nudges--next/src/common/constants.tsx';
import { InlineEditListNudgeAsync } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/list-nudge-tour/inline-edit-list/async.tsx';
import type { CellNavigationProps } from '../../../common/types/cell-navigation.tsx';
import { TextFieldWrapper } from '../../../common/ui/list-cell/styled.tsx';
import { ClickToEditOnboardingTour } from './copy-paste-onboarding/index.tsx';
import messages from './messages.tsx';

type TextCellProps = {
	issueKey: IssueKey;
	isEditable: boolean;
	isNudgeCell?: boolean;
	fieldKey: string;
	fieldType: string;
	fieldValue: string | null;
	fieldName?: string;
	testId?: string;
	issueType?: string;
	issueTypeId?: string;
	issueId: string;
} & CellNavigationProps;

type TextCellContentProps = {
	editMode: boolean;
	highlight?: string[];
	fieldName?: string;
} & TextFieldProps;

export const TextCellContent = (props: TextCellContentProps) => {
	const { editMode, highlight, fieldName, onSubmit, onBlur, onChange, onKeyDown, value, testId } =
		props;
	if (editMode) {
		return (
			<form onSubmit={onSubmit ?? noop}>
				<Textfield
					testId={testId ? `${testId}-field` : undefined}
					autoFocus
					onBlur={onBlur}
					value={value}
					onChange={onChange}
					onKeyDown={onKeyDown}
					aria-label={fieldName}
				/>
			</form>
		);
	}

	if (
		value &&
		typeof value === 'string' &&
		isSafeUrl(value) &&
		expValEquals('jsw_perf_format-links-in-custom-fields', 'list-view', true)
	) {
		return <LazySingleLineSmartLink anchorTarget="_blank" url={value} fieldType="text" />;
	}

	if (highlight && highlight.length > 0) {
		return (
			<span data-testid={testId}>
				{/* @ts-expect-error - TS2322 - Type 'string | number | readonly string[] | undefined' is not assignable to type 'string'. */}
				<HighlightText highlight={highlight} text={value} />
			</span>
		);
	}

	return <span data-testid={testId}>{value}</span>;
};

export const TextCell = ({
	issueKey,
	fieldKey,
	isEditable,
	fieldValue,
	fieldType,
	testId,
	isNudgeCell = false,
	isSelected,
	wrapperRef,
	onFocus,
	onBlur,
	onKeyDown,
	issueId,
	editMode: editModeNew = false,
	setEditMode: setEditModeNew = noop,
	cellRef,
	issueType = '',
	issueTypeId = '',
	fieldName = '',
	inputValue,
	onInputChange = noop,
}: TextCellProps): JSX.Element => {
	const searchHighlight = useSimpleSearchHighlight();

	const [textValueOld, setTextValueOld] = useState(fieldValue || '');

	const textValue = fg('jira_list_keyboard_nav') ? (inputValue ?? fieldValue) || '' : textValueOld;
	const setTextValue = fg('jira_list_keyboard_nav') ? onInputChange : setTextValueOld;

	const [editMode, setEditMode] = fg('jira_list_keyboard_nav')
		? [editModeNew, setEditModeNew]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useState<boolean>(false);

	const [{ value, error }, { saveValue, resetError }] = useEditField<string | null, null>({
		fieldKey,
		issueId,
		issueKey,
		fieldType,
		initialValue: fieldValue,
		onFailure: () => {
			setTextValue(value || '');
		},
	});
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { ref: inlineEditNudgeRef } = useRegisterNudgeTarget<HTMLDivElement>(
		INLINE_EDIT_LIST_NUDGE_ID,
		isNudgeCell,
	);
	useEffect(() => {
		if (wrapperRef?.current && fg('jira_list_keyboard_nav')) {
			inlineEditNudgeRef.current = wrapperRef.current;
		}
	}, [inlineEditNudgeRef, wrapperRef]);

	useEffect(() => {
		if (fieldValue) {
			setTextValue(fieldValue);
		}
	}, [fieldValue, setTextValue]);

	useEffect(
		() => {
			// when the value (from useEditField) changes, update the textValue (from the input field).
			if (value !== textValue && fg('jwm_list_new_copy_paste_behaviour')) {
				setTextValue(value || '');
			}
		},
		// we only care about value changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[setTextValue, value],
	);

	/**
	 * To be deleted when jira_list_keyboard_nav is cleaned up
	 * @deprecated Use handleClick in CellNavigation for generic behaviour or useImperativeHandle in this
	 * cell for custom behaviour
	 */
	const handleClick = useCallback(() => {
		if (isEditable && !editMode) {
			setEditMode(true);
			setTextValue(value || '');
		}
	}, [editMode, isEditable, setTextValue, value, setEditMode]);

	/**
	 * To be deleted when jira_list_keyboard_nav is cleaned up
	 * @deprecated Use handleKeyDown in CellNavigation for generic behaviour or handleKeyDownNew in this
	 * cell for custom behaviour
	 */
	const handleKeyDown = useCallback(
		(event: KeyboardEvent<HTMLDivElement>) => {
			event.stopPropagation();
			if (event.key === 'Escape') {
				setTextValue(value || '');
				wrapperRef?.current?.focus();
			}
		},
		[setTextValue, value, wrapperRef],
	);

	const handleKeyDownNew = useCallback(
		(event: KeyboardEvent<HTMLElement>) => {
			if (editMode && event.code === 'Escape') {
				setTextValue(value || '');
			}
			return false;
		},
		[editMode, setTextValue, value],
	);

	const saveAndClose = useCallback(
		(event: FocusEvent<HTMLElement> | FormEvent<HTMLInputElement>) => {
			if (!fg('jira_list_keyboard_nav')) {
				event.preventDefault();
			}
			if (editMode) {
				setEditMode(false);
				if (textValue !== value) {
					saveValue(textValue, null, createAnalyticsEvent({}));
				}
			}
		},
		[editMode, textValue, value, saveValue, createAnalyticsEvent, setEditMode],
	);

	const handleBlur = useCallback(
		(event: FocusEvent<HTMLElement>) => {
			saveAndClose(event);

			if (!fg('jira_list_keyboard_nav')) {
				onBlur && onBlur(event);
			}
		},
		[onBlur, saveAndClose],
	);

	const handleChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			setTextValue(event.target.value);
			resetError();
		},
		[resetError, setTextValue],
	);

	const renderNudgeWrapper = (children: ReactNode) => (
		<InlineEditListNudgeAsync>{children}</InlineEditListNudgeAsync>
	);

	/**
	 * To be deleted when jira_list_keyboard_nav is cleaned up
	 * @deprecated wrapperRef is now managed in CellNavigation
	 */
	const onWrapperRef = useCallback(
		(ref: ElementRef<'div'> | null) => {
			if (wrapperRef) {
				// eslint-disable-next-line no-param-reassign
				wrapperRef.current = ref;
			}
			inlineEditNudgeRef.current = ref;
		},
		[inlineEditNudgeRef, wrapperRef],
	);

	const handleSubmit = useCallback(
		(e: FormEvent<HTMLInputElement>) => {
			saveAndClose(e);
			if (!fg('jira_list_keyboard_nav')) {
				wrapperRef?.current?.focus();
			}
		},
		[saveAndClose, wrapperRef],
	);

	const cellOverrides = useCallback(
		() => ({
			onKeyDown: handleKeyDownNew,
		}),
		[handleKeyDownNew],
	);
	useImperativeHandle(cellRef, cellOverrides, [cellOverrides]);

	const handleSpotlightClose = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			// Need to stop the event as its auto focusing on the cell and putting it in edit mode
			event.stopPropagation();

			setEditMode(false);

			setTimeout(() => {
				wrapperRef?.current?.focus();
			}, 0);
		},
		[setEditMode, wrapperRef],
	);

	const textCell = (
		<>
			<TextCellContent
				editMode={editMode}
				onBlur={handleBlur}
				onSubmit={handleSubmit}
				onKeyDown={fg('jira_list_keyboard_nav') ? noop : handleKeyDown}
				onChange={handleChange}
				value={textValue}
				highlight={searchHighlight}
				testId={testId}
				fieldName={fieldName}
			/>

			{error && (
				<FixItErrorFlag
					error={error}
					title={messages.errorTitle}
					description={messages.errorMessage}
					issueType={issueType}
					issueTypeId={issueTypeId}
					fieldId={fieldKey}
					fieldName={fieldName}
				/>
			)}
		</>
	);

	const textCellWrapper = (
		<ConditionalWrapper condition={isNudgeCell} renderWrapper={renderNudgeWrapper}>
			<TextCellWrapper
				isEditable={isEditable}
				testId="business-list.ui.list-view.text-cell.text-cell-wrapper"
			>
				{textCell}
			</TextCellWrapper>
		</ConditionalWrapper>
	);

	if (fg('jwm_list_new_copy_paste_behaviour')) {
		return (
			<ClickToEditOnboardingTour
				isNudgeCell={isNudgeCell}
				wrapperRef={wrapperRef}
				onClose={handleSpotlightClose}
			>
				{textCellWrapper}
			</ClickToEditOnboardingTour>
		);
	}

	if (fg('jira_list_keyboard_nav')) {
		return <>{textCellWrapper}</>;
	}

	return (
		<ConditionalWrapper condition={isNudgeCell} renderWrapper={renderNudgeWrapper}>
			<TextFieldWrapper
				onClick={handleClick}
				isEditable={isEditable}
				onFocus={onFocus}
				onBlur={onBlur}
				isSelected={isSelected}
				tabIndex={0}
				onKeyDown={onKeyDown}
				ref={onWrapperRef}
			>
				{textCell}
			</TextFieldWrapper>
		</ConditionalWrapper>
	);
};
